import { useEffect, useMemo, useState } from 'react';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { ALL_PLEDGES } from './pledgesList.js';
import { getEnabledPledgeKeys } from './enabledPledgesStorage.js';
import allegianceBg from '../../../../../assets/pledges/allegiance.png';
import christianFlagBg from '../../../../../assets/pledges/christian.png';
import bibleBg from '../../../../../assets/pledges/bible.png';
import styles from './Pledges.module.css';

/** Pledge-key → background-image URL. Pledges without an entry just
 *  render the plain card background (cream). */
const PLEDGE_BACKGROUNDS: Record<string, string> = {
  allegiance: allegianceBg,
  'christian-flag': christianFlagBg,
  bible: bibleBg,
};

export function Pledges() {
  const { active } = useActiveClassroom();
  const [index, setIndex] = useState(0);

  // Filter the master pledge list by the per-classroom enabled set.
  // Re-reads when the active classroom changes.
  const pledges = useMemo(() => {
    if (!active) return [];
    const enabled = new Set(getEnabledPledgeKeys(active.id));
    return ALL_PLEDGES.filter((p) => enabled.has(p.key));
  }, [active]);

  // Snap back if the enabled list shrinks past the current index.
  useEffect(() => {
    if (index >= pledges.length) setIndex(0);
  }, [index, pledges.length]);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  if (pledges.length === 0) {
    return (
      <section className={styles.empty} role="status">
        No pledges enabled. Open the classroom&apos;s Pledges settings to pick at least one.
      </section>
    );
  }

  function go(delta: number) {
    setIndex((i) => (i + delta + pledges.length) % pledges.length);
  }

  const pledge = pledges[index]!;
  const bg = PLEDGE_BACKGROUNDS[pledge.key];
  // Show the prev/next arrows only when there's more than one pledge to flip
  // through — they signal the card can be scrolled.
  const multiple = pledges.length > 1;

  return (
    <section className={styles.pledges} data-testid="pledges-page">
      <div className={styles.stage}>
        <button
          type="button"
          onClick={() => go(1)}
          className={styles.card}
          data-testid="pledges-card"
          data-pledge-key={pledge.key}
          aria-label={`Tap to advance. Currently showing ${pledge.title}.`}
        >
          {bg ? <img src={bg} alt="" aria-hidden="true" className={styles.cardBg} /> : null}
          <h1 className={styles.cardTitle}>{pledge.title}</h1>
          <p className={styles.cardBody}>{pledge.body}</p>
        </button>

        {multiple ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              className={`${styles.arrow} ${styles.arrowLeft}`}
              aria-label="Previous pledge"
              data-testid="pledges-prev"
            >
              <Chevron dir="left" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className={`${styles.arrow} ${styles.arrowRight}`}
              aria-label="Next pledge"
              data-testid="pledges-next"
            >
              <Chevron dir="right" />
            </button>
          </>
        ) : null}
      </div>

      <div className={styles.dots} role="tablist" aria-label="Pledge slide">
        {pledges.map((p, i) => (
          <span
            key={p.key}
            role="tab"
            aria-selected={i === index}
            data-active={i === index}
            data-testid="pledges-dot"
            className={styles.dot}
          />
        ))}
      </div>
    </section>
  );
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
  );
}
